// Shared react-dnd item types so Layers rows, Library chips, and canvas slots
// exchange the same drag payloads.
export const HOME_COMPONENT_DRAG_TYPE = 'native-home-component'
export const HOME_LIBRARY_DRAG_TYPE = 'native-home-library'
export const HOME_CARD_DRAG_TYPE = 'native-home-card'

/**
 * Sortable hover rule from the react-dnd sortable example: only reorder once the
 * pointer has crossed the hovered item's midpoint in the direction of travel.
 * Reordering on every hover event makes the moved item swap back and forth under
 * the pointer, which reads as flicker.
 */
export function crossedMidpoint(element, monitor, fromIndex, toIndex, axis = 'y') {
  const rect = element?.getBoundingClientRect()
  const offset = monitor.getClientOffset()
  if (!rect || !offset) return false
  const middle = axis === 'y' ? (rect.top + rect.bottom) / 2 : (rect.left + rect.right) / 2
  const pointer = axis === 'y' ? offset.y : offset.x
  if (fromIndex < toIndex && pointer < middle) return false
  if (fromIndex > toIndex && pointer > middle) return false
  return true
}

/**
 * The part of an element the operator can actually see: its box clipped by every
 * ancestor that scrolls or clips, then by the viewport. Preview sections can be
 * thousands of pixels tall and carousel cards sit mostly off-screen, so drop
 * decisions on the phone use the visible part rather than the whole box.
 */
const CLIPPING_OVERFLOW = new Set(['auto', 'scroll', 'hidden', 'clip'])

export function visibleRect(element) {
  const rect = element?.getBoundingClientRect()
  if (!rect) return null
  let { top, right, bottom, left } = rect
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowX, overflowY } = window.getComputedStyle(node)
    if (!CLIPPING_OVERFLOW.has(overflowX) && !CLIPPING_OVERFLOW.has(overflowY)) continue
    const clip = node.getBoundingClientRect()
    top = Math.max(top, clip.top)
    left = Math.max(left, clip.left)
    bottom = Math.min(bottom, clip.bottom)
    right = Math.min(right, clip.right)
  }
  top = Math.max(top, 0)
  left = Math.max(left, 0)
  bottom = Math.min(bottom, window.innerHeight)
  right = Math.min(right, window.innerWidth)
  if (bottom <= top || right <= left) return null
  return { top, right, bottom, left }
}

/**
 * Which side of a hovered item a dragged item lands on: 'before' while the pointer
 * is in the first half of the item's visible part, 'after' past it, null when the
 * item is not visible at all. react-dnd runs `hover` before it stores the offset
 * that triggered it, so callers that track the pointer from their own drag events
 * pass it in; the monitor's (one event stale) offset is the fallback.
 */
export function dropEdge(element, monitor, axis = 'y', pointer = monitor.getClientOffset()) {
  const rect = visibleRect(element)
  const offset = pointer || monitor.getClientOffset()
  if (!rect || !offset) return null
  const middle = axis === 'y' ? (rect.top + rect.bottom) / 2 : (rect.left + rect.right) / 2
  const position = axis === 'y' ? offset.y : offset.x
  return position < middle ? 'before' : 'after'
}

/**
 * The list position an item dragged from `from` takes when dropped on `edge` of
 * the item currently at `index`, once its own slot is vacated.
 */
export function dropIndex(from, index, edge) {
  const target = edge === 'after' ? index + 1 : index
  return from < target ? target - 1 : target
}
